import { AxiosResponse } from 'axios';
import { ALL_PERMISSIONS } from '../index';
import Entity from './shared/entity';
import { Event } from './shared/eventstore-client/EventstoreClient';
import { ServiceClientWithEventstore } from './shared/service-client/ServiceClientWithEventstore';

export interface MessageResponse {
  message: string;
}
// bump
export interface TokenResponse {
  token: string;
}

export interface IdentityProperties {
  custodian?: string | null;
  permissions?: ALL_PERMISSIONS;
}

export interface Identity extends Entity, IdentityProperties {}

export interface GetIdentitiesResponse {
  identities: Identity[];
  continuationToken?: string;
}

export interface GetIdentitiesRequest {
  limit?: number;
  continuationToken?: string;
}

export enum IdentityType {
  Admin = 'Admin',
  User = 'User',
  Custodian = 'Custodian',
}

export interface ConnectIdentity extends Identity {
  type: IdentityType;
}

export enum ConnectionType {
  PASSWORDLESS = 'PASSWORDLESS',
  USERNAME = 'USERNAME',
}

export interface User {
  id: string;
  username: string;
  email?: string;
  phoneNumber?: string;
  nickname: string;
  name?: string;
  userMetadata: {
    [key: string]: string;
  };
  appMetadata: {
    useMfa: boolean;
    identity: Pick<ConnectIdentity, 'id'> & Partial<ConnectIdentity>;
    termsAccepted: boolean;
  };
  connection?: ConnectionType;
}

export type CreateIdentity = IdentityProperties;
export type UpdateIdentity = Partial<IdentityProperties>;

export type PERMISSIONS =
  | typeof CREATE
  | typeof CREATE_SAME_CUSTODIAN
  | typeof CREATE_ANY
  | typeof CREATE_PERMISSIONS
  | typeof DELETE
  | typeof GET_ALL
  | typeof GET
  | typeof UPDATE
  | typeof UPDATE_PERMISSIONS
  | typeof UPDATE_ANY
  | typeof UPDATE_SAME_CUSTODIAN
  | typeof CREATE_API_KEY
  | typeof GET_EVENTS_ANY
  | typeof GET_EVENTS_CREATED
  | typeof GET_EVENTS_DELETED
  | typeof GET_EVENTS_UPDATED
  | typeof GENERATE_TOKEN;

/* Allows creation of identities without custodian */
export const CREATE = 'IDENTITY:CREATE';
/* Allows creation of identities with custodian, as long as custodian is same as the invoking identity's custodian */
export const CREATE_SAME_CUSTODIAN = 'IDENTITY:CREATE:SAME_CUSTODIAN';
/* Allows creation of identities with custodian, regardless of which custodian the invoking identity belongs to */
export const CREATE_ANY = 'IDENTITY:CREATE:ANY';
/* Required to create an identity with additional permissions. Should be combined with one or more of the above permissions to create */
export const CREATE_PERMISSIONS = 'IDENTITY:CREATE:PERMISSIONS';
export const CREATE_API_KEY = 'IDENTITY:CREATE:API_KEY';
export const DELETE = 'IDENTITY:DELETE';
export const GET = 'IDENTITY:GET';
export const GET_ALL = 'IDENTITY:GET_ALL';
/* Allows the calling identity to update itself only */
export const UPDATE = 'IDENTITY:UPDATE';
/* Allows the calling identity to update identities in same custodian as caller */
export const UPDATE_SAME_CUSTODIAN = 'IDENTITY:UPDATE:SAME_CUSTODIAN';
/* Allows the calling identity to update any identity, regardless of which custodian the calling idenity belongs to */
export const UPDATE_ANY = 'IDENTITY:UPDATE:ANY';
/* Required to update an identity's permissions. Should be combined with one or more of the above permissions to update */
export const UPDATE_PERMISSIONS = 'IDENTITY:UPDATE:PERMISSIONS';

export const GET_EVENTS_ANY = 'IDENTITY:GET_EVENTS:ANY';
export const GET_EVENTS_CREATED = 'IDENTITY:GET_EVENTS:CREATED';
export const GET_EVENTS_DELETED = 'IDENTITY:GET_EVENTS:DELETED';
export const GET_EVENTS_UPDATED = 'IDENTITY:GET_EVENTS:UPDATED';

export const GENERATE_TOKEN = 'IDENTITY:GENERATE_TOKEN';

export const PERMISSION_NAMES = {
  CREATE,
  CREATE_SAME_CUSTODIAN,
  CREATE_ANY,
  CREATE_PERMISSIONS,
  DELETE,
  GET,
  GET_ALL,
  UPDATE,
  UPDATE_PERMISSIONS,
  UPDATE_ANY,
  UPDATE_SAME_CUSTODIAN,
  CREATE_API_KEY,
  GET_EVENTS_ANY,
  GET_EVENTS_CREATED,
  GET_EVENTS_DELETED,
  GET_EVENTS_UPDATED,
  GENERATE_TOKEN,
};

export interface CreatedEvent extends Event {
  type: 'CREATED';
  data: Identity;
}
export interface UpdatedEvent extends Event {
  type: 'UPDATED';
  data: UpdateIdentity;
}
export interface DeletedEvent extends Event {
  type: 'DELETED';
}

export type IdentityEvents = CreatedEvent | DeletedEvent | UpdatedEvent;

export class IdentityClient extends ServiceClientWithEventstore<IdentityEvents> {
  public async getAll(options?: GetIdentitiesRequest): Promise<AxiosResponse<GetIdentitiesResponse>> {
    return await this.httpClient.get('/', { params: options, ...this.requestConfig });
  }

  public async get(identityId: string): Promise<AxiosResponse<Identity>> {
    return await this.httpClient.get(`/${identityId}`, this.requestConfig);
  }

  public async create(newIdentity: CreateIdentity): Promise<AxiosResponse<Identity>> {
    return await this.httpClient.post('/', newIdentity, this.requestConfig);
  }

  public async createApiKey(identityId: string): Promise<AxiosResponse<{ apiKey: string }>> {
    return await this.httpClient.post(`/${identityId}/api-key`, undefined, this.requestConfig);
  }

  public async update(identityId: string, updateIdentity: UpdateIdentity): Promise<AxiosResponse<Identity>> {
    return await this.httpClient.patch(`/${identityId}`, updateIdentity, this.requestConfig);
  }

  public async delete(identityId: string): Promise<AxiosResponse<MessageResponse>> {
    return await this.httpClient.delete(`/${identityId}`, this.requestConfig);
  }

  public async generateToken(identityId: string): Promise<AxiosResponse<TokenResponse>> {
    return await this.httpClient.post(`/${identityId}/token`, undefined, this.requestConfig);
  }
  public async acceptTerms(): Promise<AxiosResponse<void>> {
    return await this.httpClient.post(`/accept-terms`, undefined, this.requestConfig);
  }
  public async searchPasswordless(phoneOrEmail: string): Promise<AxiosResponse<User[]>> {
    return await this.httpClient.get(`/search`, { ...this.requestConfig, params: { search: phoneOrEmail } });
  }
}
